import React, { memo } from 'react';
import { UniversalLink } from '@plone/volto/components';

import useAccessibleBg from '../../../../../hooks/useAccessibleBg';
import { getResponsiveImageSources } from '../../../../shared/OptimizedImage/optimizedImage';

import type { ProgramItem } from '../../../../shared/ProgramCarouselSection/ProgramCarouselSection.types';

type Props = {
  items: ProgramItem[];
  loading?: boolean;
  loadingOverlay?: boolean;
  emptyMessage?: string;
  searchTerm?: string;
};

function normalizeText(value = '') {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function HighlightText({ text, term }: { text: string; term?: string }) {
  const cleanTerm = term?.trim();

  if (!cleanTerm) return <>{text}</>;

  const normalizedText = normalizeText(text);
  const normalizedTerm = normalizeText(cleanTerm);
  const index = normalizedText.indexOf(normalizedTerm);

  if (index < 0) return <>{text}</>;

  const before = text.slice(0, index);
  const match = text.slice(index, index + cleanTerm.length);
  const after = text.slice(index + cleanTerm.length);

  return (
    <>
      {before}
      <mark className="program-card__highlight">{match}</mark>
      {after}
    </>
  );
}

function ProgramsGridSkeletonCard() {
  return (
    <article className="programs-grid__item programs-grid__item--loading">
      <div className="programs-grid__card program-card programs-grid__card--loading">
        <div className="program-card__media">
          <div className="programs-grid__loadingImage shimmer" />
        </div>

        <div className="program-card__body programs-grid__loadingBody">
          <div className="programs-grid__loadingEyebrow shimmer" />
          <div className="programs-grid__loadingTitle shimmer" />
        </div>
      </div>
    </article>
  );
}

const ProgramsGridCard = memo(function ProgramsGridCard({
  program,
  searchTerm,
}: {
  program: ProgramItem;
  searchTerm?: string;
}) {
  const accessibleBg = useAccessibleBg(program.image);
  const imageSources = getResponsiveImageSources(program.image);

  return (
    <article className="programs-grid__item">
      <UniversalLink
        href={program['@id']}
        className="programs-grid__card program-card"
        aria-label={program.title}
      >
        <div className="program-card__media">
          {imageSources ? (
            <img
              src={imageSources.src}
              srcSet={imageSources.fallbackSrcSet}
              sizes="(max-width: 480px) calc(100vw - 64px), (max-width: 767px) 280px, 280px"
              alt=""
              loading="lazy"
              decoding="async"
              width="280"
              height="170"
            />
          ) : (
            <div className="programs-grid__placeholder" aria-hidden="true" />
          )}
        </div>

        <div
          className="program-card__body"
          style={{ background: accessibleBg }}
        >
          <div className="program-card__overlay" />

          <span className="program-card__eyebrow">
            {program.type_title || 'Programa'}
          </span>

          <h2 className="program-card__title">
            <HighlightText text={program.title} term={searchTerm} />
          </h2>
        </div>
      </UniversalLink>
    </article>
  );
});

export default function ProgramsGrid({
  items,
  loading = false,
  loadingOverlay = false,
  emptyMessage = 'Nenhum item encontrado.',
  searchTerm = '',
}: Props) {
  if (loading) {
    return (
      <section
        className="programs-grid programs-grid--loading"
        aria-label="Carregando programas"
        aria-busy="true"
      >
        {Array.from({ length: 8 }).map((_, index) => (
          <ProgramsGridSkeletonCard key={index} />
        ))}
      </section>
    );
  }

  if (!items.length) {
    return (
      <section className="programs-grid programs-grid--empty">
        <div className="programs-grid__state">{emptyMessage}</div>
      </section>
    );
  }

  return (
    <section
      className={[
        'programs-grid',
        loadingOverlay ? 'programs-grid--updating' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Lista de programas"
      aria-busy={loadingOverlay}
    >
      {items.map((item) => (
        <ProgramsGridCard
          key={item['@id']}
          program={item}
          searchTerm={searchTerm}
        />
      ))}

      {loadingOverlay ? (
        <div className="programs-grid__overlay" aria-hidden="true">
          <span className="programs-grid__overlayPulse" />
        </div>
      ) : null}
    </section>
  );
}
